import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { fmtEUR } from "../lib/format";
import { RefreshCcw, ArrowLeft, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  Card, Button, SectionLabel,
} from "../components/app/theme";

/**
 * Niveau → couleurs douces alignées sur le thème "sobre premium".
 * Pas de barre colorée épaisse : accent latéral fin + fond blanc.
 */
const NIVEAU_STYLE = {
  critique:  { border: "var(--th-haut-text)",  bg: "var(--th-surface)", accent: "var(--th-haut-text)" },
  attention: { border: "var(--th-moyen-text)", bg: "var(--th-surface)", accent: "var(--th-moyen-text)" },
  action:    { border: "var(--th-navy)",       bg: "var(--th-surface)", accent: "var(--th-navy)" },
  positif:   { border: "var(--th-faible-text)",bg: "var(--th-surface)", accent: "var(--th-faible-text)" },
  info:      { border: "var(--th-border)",     bg: "var(--th-surface)", accent: "var(--th-text-3)" },
  ok:        { border: "var(--th-border)",     bg: "var(--th-surface)", accent: "var(--th-text-3)" },
};

function Highlight({ label, value }) {
  return (
    <div className="py-3" style={{ borderBottom: "1px solid var(--th-border)" }}>
      <div className="th-section-label">{label}</div>
      <div
        className="th-num mt-1"
        style={{
          fontFamily: "var(--th-font-serif)",
          fontSize: 22,
          fontWeight: 500,
          color: "var(--th-text)",
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default function MorningBrief() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/morning-brief");
      setBrief(r.data);
    } catch {
      toast.error("Impossible de générer le brief");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyBrief = async () => {
    if (!brief) return;
    const text = [
      brief.greeting,
      "",
      ...brief.paragraphs.flatMap((p) => [`# ${p.titre}`, p.texte, ""]),
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Brief copié dans le presse-papier");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copie impossible — sélectionnez le texte manuellement");
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-sm" style={{ color: "var(--th-text-3)" }}>
        Génération du brief…
      </div>
    );
  }
  if (!brief) return null;

  return (
    <div
      className="th-scope p-6 md:p-10 max-w-5xl"
      style={{ backgroundColor: "var(--th-bg)" }}
      data-testid="morning-brief-page"
    >
      <header className="mb-8 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <button
            onClick={() => nav("/dashboard")}
            className="text-xs flex items-center gap-1.5 mb-3 hover:underline"
            style={{ color: "var(--th-text-3)" }}
            data-testid="brief-back-to-dashboard"
          >
            <ArrowLeft size={12} /> Retour à la vue dirigeant
          </button>
          <SectionLabel>Brief matinal · dirigeant</SectionLabel>
          <h1
            className="mt-1 leading-tight"
            style={{
              fontFamily: "var(--th-font-serif)",
              fontSize: 27,
              fontWeight: 500,
              letterSpacing: "-0.005em",
              color: "var(--th-text)",
            }}
          >
            {brief.greeting}
          </h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--th-text-2)" }}>
            {brief.headline}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="primary" onClick={copyBrief} data-testid="brief-copy-btn">
            {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
            {copied ? "Copié" : "Copier le brief"}
          </Button>
          <Button variant="secondary" onClick={load}>
            <RefreshCcw size={12} /> Actualiser
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8">
        {/* Corps narratif */}
        <article className="space-y-4" data-testid="brief-paragraphs">
          {brief.paragraphs.map((p, i) => {
            const s = NIVEAU_STYLE[p.niveau] || NIVEAU_STYLE.info;
            return (
              <section
                key={`p-${i}`}
                data-testid={`brief-paragraph-${p.niveau}`}
                className="p-5"
                style={{
                  backgroundColor: s.bg,
                  border: "1px solid var(--th-border)",
                  borderLeft: `2px solid ${s.accent}`,
                  borderRadius: 12,
                }}
              >
                <div className="th-section-label mb-2" style={{ color: s.accent }}>
                  {p.titre}
                </div>
                <p
                  className="text-sm md:text-[15px] leading-relaxed"
                  style={{ color: "var(--th-text)" }}
                >
                  {p.texte}
                </p>
              </section>
            );
          })}
          <div className="text-xs pt-2" style={{ color: "var(--th-text-3)" }}>
            Brief généré le {new Date(brief.generated_at).toLocaleString("fr-FR")} à partir des données importées. Aucune IA, calcul purement déterministe.
          </div>
        </article>

        {/* Chiffres clés à droite */}
        <aside className="hidden lg:block">
          <Card className="p-5 sticky top-6">
            <SectionLabel className="mb-3">Chiffres du jour</SectionLabel>
            <Highlight label="Encours propales" value={fmtEUR(brief.highlights.encours_montant)} />
            <Highlight label="Nombre de dossiers" value={brief.highlights.encours_nombre} />
            <Highlight
              label="Haut risque"
              value={`${brief.highlights.haut_nombre} · ${fmtEUR(brief.highlights.haut_montant)}`}
            />
            <Highlight label="CA dormant" value={fmtEUR(brief.highlights.ca_dormant)} />
            <Highlight label="Clients à risque" value={brief.highlights.nb_a_risque} />
          </Card>
        </aside>
      </div>
    </div>
  );
}
